import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "config/firebase";
import { useEffect, useState } from "react";
import { useSessionUser } from "hooks/useSession";

const profileImageKey = (userId?: string | number, path?: string | null) =>
  ["profile", "image", userId ?? null, path ?? null] as const;

export const useProfileImage = () => {
  const queryClient = useQueryClient();
  const { session: profile } = useSessionUser();
  const userId = profile?.user_id;
  const path = profile?.profile_image_path;
  const sessionKey = path ? `profileImage-${userId}-${path}` : `profileImage-${userId}`;
  const [hadError, setHadError] = useState(false);
  const optimisticKey = ["profile", "image", userId ?? null, "optimistic-upload"] as const;
  const optimisticQuery = useQuery(
    optimisticKey,
    async () => (queryClient.getQueryData(optimisticKey) as string | undefined) || "",
    {
      initialData: () => (queryClient.getQueryData(optimisticKey) as string | undefined) || "",
      staleTime: Infinity,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      enabled: true,
    }
  );
  const optimisticUrl = (optimisticQuery.data as string) || undefined;

  const query = useQuery(
    profileImageKey(userId, path || undefined),
    async () => {
      const cached = sessionStorage.getItem(sessionKey);
      if (cached) return cached;
      const src = await getDownloadURL(ref(storage, path!));
      sessionStorage.setItem(sessionKey, src);
      return src;
    },
    {
      enabled: !!path,
      staleTime: Infinity,
      cacheTime: 60 * 60 * 1000,
    }
  );

  useEffect(() => {
    setHadError(false);
  }, [path, query.data]);

  const onImageError = () => setHadError(true);
  const resolvedSrc = optimisticUrl ?? ((query.data as string) || "");
  const isLoading = optimisticUrl ? false : query.isLoading;
  const shouldShowImage = Boolean(resolvedSrc) && !isLoading && !hadError;

  return {
    image: { isLoading, src: resolvedSrc },
    hasImagePath: !!path,
    shouldShowImage,
    onImageError,
  };
};
